import * as React from "react";
import { connect, useSelector } from "react-redux";
import { values, flatten, uniq } from "lodash";
import { Container, Header, Loader, Button, Segment } from "semantic-ui-react";
import isPostcodeValid from "uk-postcode-validator";
import { createValidator, validators } from "not-valid";

import { QuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import {
    clearQuestionnaire,
    openSection,
    loadQuestionnaire as loadQuestionnaireAction,
    removeQuestion,
    updateQuestion,
    submitPhysiotherapyReferralQuestionnaire
} from "@teamprevent/common/questionnaires/actions";
import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import {
    FileAnswer, AnsweredQuestion, AnsweredQuestionWithId, QuestionAnswer, QuestionnairesState, QuestionnaireTypes
} from "@teamprevent/common/questionnaires/model";
import { FileQuestion } from "@teamprevent/common/questionnaires/components/questions/FileQuestion";
import { OrgLevel } from "@teamprevent/common/questionnaires/model";

import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { BODY_PARTS, CURRENT_WORK_STATUS, PhysiotherapyReferralFormProps, PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY } from "../models";
import * as questionIds from "../../questionnaires/questionnaireConfig/physiotherapySelfReferral/questionIds";
import { usePrevious } from "../../customHooks";
import { CurrentUser } from "../../auth/roles";
import { ReferralFormState } from "../../questionnaires/components/referralForm/models";

import { PhysiotherapyReferralFormState } from "../../questionnaires/components/physiotherapyReferralForm/models";

import { Location } from "redux-little-router";

interface DispatchProps {
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion; title?: string }) => void;
    removeQuestion: (payload: { sectionId: string; id: string }) => void;
    openFirstSection: () => void;
    clearQuestionnaire: () => void;
    loadQuestionnaire: (apiUrl: string, customerId: string, questionnaireId: string, caseId: string) => void;
    submitQuestionnaire: (apiUrl: string, questions: AnsweredQuestionWithId[], linkedOhCaseId: string) => Promise<string>;
}

interface OwnProps {
    applicationType?: string;
}

type AllProps = PhysiotherapyReferralFormProps & DispatchProps & OwnProps;

const PhysiotherapySelfReferralUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const employeeDetailSectionId: string = "ReferredDetails";
    const reasonsForReferralSectionId: string = "ReasonsForReferral";
    const attachmentsSectionId: string = "Attachments";
    const orgLevelQuestionId: string = "OrgLevel";
    const furtherDetailsSectionId: string = "FurtherDetails";

    const furtherDetailsSectionTitle: string = "Further Details";

    const currentWorkStatus = { id: questionIds.CurrentWorkStatus, notAtWorkAnswer: "Not at work" };

    const ohCaseId = useSelector((state: { router: Location }) => state.router.params?.ohCaseId);
    const customerId = useSelector((state: { router: Location }) => state.router.params?.customerId);
    const linkedOhCaseId = useSelector((state: { router: Location }) => state.router.params?.linkedOhCaseId);
    const currentUser = useSelector((state: { currentUser: CurrentUser }) => state.currentUser);
    const formQuestions: AnsweredQuestionWithId[] = useSelector((state: { questionnaires: QuestionnairesState }) =>
        state.questionnaires[PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY]
            ? uniq(flatten(state.questionnaires[PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY].sections.map(section => values(section.questions))))
            : []
    );
    const questionnaireId: string = useSelector((state: { router: Location }) => state.router.params?.[PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY]);
    const orgLevels: OrgLevel[] = useSelector((state: { referralForm: ReferralFormState; physiotherapyReferralForm: PhysiotherapyReferralFormState }) =>
        state.referralForm?.orgLevels ?? state.physiotherapyReferralForm?.orgLevels);

    const [documentCount, setDocumentCount] = React.useState<number>(0);
    const [selectedOrgStructureLevelIds, setSelectedOrgStructureLevelIds] = React.useState<string[]>([]);
    const previousQuestions: AnsweredQuestionWithId[] = usePrevious(formQuestions) ?? [];
    const api = props.api || "/api/physiotherapy";
    const canSubmitFormWhenOrgLevelsAreDisabled = getFeatureToggles()[FeatureToggleNames.CanSubmitFormWhenOrgLevelsAreDisabled];

    React.useEffect(() => {
        props.openFirstSection();
        if (!props.readonly) {
            props.loadOrgStructure("");
        }

        return props.clearQuestionnaire;
    }, []);

    React.useEffect(() => {
        if (props.readonly) {
            return;
        }

        const updateOrgLevel = (level: number, questionId: string) => {
            const oldOrgLevel = (previousQuestions.find(question => question.id === questionId) &&
                previousQuestions.find(question => question.id === questionId).answer);
            const newOrgLevel = getAnswerToQuestionWithId(questionId);
            if (newOrgLevel && oldOrgLevel !== newOrgLevel) {
                changeOrgStructureLevel(level, newOrgLevel as string);
            }
        };
        updateOrgLevel(0, questionIds.OrgLevel1Id);
        updateOrgLevel(1, questionIds.OrgLevel2Id);
        updateOrgLevel(2, questionIds.OrgLevel3Id);
        updateOrgLevel(3, questionIds.OrgLevel4Id);
    });

    const loadQuestionnaire = (): void => {
        if (!linkedOhCaseId && (questionnaireId || ohCaseId)) {
            props.loadQuestionnaire(api, customerId, questionnaireId, ohCaseId);
        }
    };

    const trimAnswerAndUpdateEmployeeQuestion = (questionId: string, questions: AnsweredQuestionWithId[]) => {

        const stateQuestion = formQuestions.find(q => q.id === questionId);

        if (stateQuestion) {
            props.updateAnswer({
                sectionId: employeeDetailSectionId,
                id: stateQuestion.id,
                question: { ...stateQuestion, answer: stateQuestion.answer?.toString()?.trim() }
            });
        }

        const question = questions.find(q => q.id === questionId);

        if (question) {
            question.answer = question.answer?.toString()?.trim();
        }
    };

    const submitQuestionnaire = async (questions: AnsweredQuestionWithId[]): Promise<boolean> => {
        trimAnswerAndUpdateEmployeeQuestion(questionIds.JobTitle, questions);

        const questionnaireValid = formQuestions.every(question => question.isValid);
        const newOhCaseId = await props.submitQuestionnaire(api, questions, linkedOhCaseId);
        if (questionnaireValid) {
            props.afterSubmit(newOhCaseId);
            return true;
        } else {
            return false;
        }
    };

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return props.questionnaireConfig.questions.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAnswerToQuestionWithId = (id: string): QuestionAnswer => {
        return formQuestions.find(question => question.id === id)?.answer;
    };

    const addDocument = (): void => {
        props.updateAnswer({
            sectionId: attachmentsSectionId,
            id: `Document${documentCount + 1}`,
            question: { question: "", answer: { blobId: "", fileName: "" } }
        });
        setDocumentCount(documentCount + 1);
    };

    const changeOrgStructureLevel = (level: number, selectedId: string): void => {
        setSelectedOrgStructureLevelIds([...selectedOrgStructureLevelIds.slice(0, level), selectedId]);
        const selectedOrgLevel = orgLevels.find(orgLevel => orgLevel.id === selectedId);
        if (!selectedOrgLevel) {
            return;
        }

        const selectedName = selectedOrgLevel.name;
        const thisOrgLevelNameQuestion = formQuestions.find(question => question.id === `${orgLevelQuestionId}${level + 1}Name`);

        if (!canSubmitFormWhenOrgLevelsAreDisabled || thisOrgLevelNameQuestion) {
            props.updateAnswer({
                sectionId: furtherDetailsSectionId,
                id: thisOrgLevelNameQuestion.id,
                question: { ...thisOrgLevelNameQuestion, answer: selectedName },
                title: furtherDetailsSectionTitle
            });
        }

        for (let i = level + 2; i <= 4; i++) {
            const orgLevelIdQuestion = formQuestions.find(question => question.id === `${orgLevelQuestionId}${i}Id`);

            if (!canSubmitFormWhenOrgLevelsAreDisabled || orgLevelIdQuestion) {
                props.updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelIdQuestion.id,
                    question: { ...orgLevelIdQuestion, answer: "" },
                    title: furtherDetailsSectionTitle
                });
            }

            const orgLevelNameQuestion = formQuestions.find(question => question.id === `${orgLevelQuestionId}${i}Name`);

            if (!canSubmitFormWhenOrgLevelsAreDisabled || orgLevelNameQuestion) {
                props.updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelNameQuestion.id,
                    question: { ...orgLevelNameQuestion, answer: "" },
                    title: furtherDetailsSectionTitle
                });
            }
        }
        props.loadOrgStructure(selectedId);
    };

    return (
        <Container className="physiotherapy-form">
            {props.readonly ? <Container>
                <Segment basic>
                    <Header as="h1">Referral</Header>
                </Segment>
            </Container>
                :
                <Container>
                    <Segment basic>
                        <Header as="h1">Physiotherapy Referral</Header>
                    </Segment>
                </Container>}
            {currentUser.id || props.readonly ?
                <OldProgressBarQuestionnaireContainer
                    loadQuestionnaire={loadQuestionnaire}
                    submitQuestionnaire={submitQuestionnaire}
                    readonly={props.readonly}
                    questionnaireKey={PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY}
                    questionnaireType={QuestionnaireTypes.Other}
                >
                    <OldProgressBarQuestionnaireSection title="Data Privacy" id="DataPrivacy">
                        <p>
                            Please tick all that apply
                        </p>
                        <CheckboxQuestion
                            questionConfig={getQuestionConfig(questionIds.PrivacyConsent)}
                            readonly={props.readonly}
                            value="Granted"
                        />
                        <CheckboxQuestion
                            questionConfig={getQuestionConfig(questionIds.DataSecureConsent)}
                            readonly={props.readonly}
                            value="Granted"
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.PurchaseOrderNumber)}
                            readonly={props.readonly}
                            hidden
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Employee Details" id={employeeDetailSectionId}>
                        <TextQuestion hideDivider questionConfig={getQuestionConfig(questionIds.JobTitle)} readonly={props.readonly} mandatory />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.Postcode)}
                            readonly={props.readonly}
                            validators={[createValidator(postcode => isPostcodeValid(postcode), "Postcode is not valid")]}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.MobilePhone)}
                            readonly={props.readonly}
                            validators={[validators.validPhoneNumber(10)]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.HomePhone)}
                            readonly={props.readonly}
                            validators={[validators.validPhoneNumber(10)]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.EmployeeId)}
                            readonly={props.readonly}
                            hideDivider
                            validators={[ validators.validLength({ max: 20 }) ]}
                        />
                        {/* Organisational levels */}
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel1Name), id: questionIds.OrgLevel1Id })}
                            options={orgLevels?.filter(orgLevel => !orgLevel.parentId)
                                .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(0, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly}
                            hideDivider
                            mandatory
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel1Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel2Name), id: questionIds.OrgLevel2Id })}
                            options={orgLevels && selectedOrgStructureLevelIds[0] &&
                                orgLevels.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[0])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(1, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly || !selectedOrgStructureLevelIds[0]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel2Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel3Name), id: questionIds.OrgLevel3Id })}
                            options={orgLevels && selectedOrgStructureLevelIds[1] &&
                                orgLevels.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[1])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(2, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly || !selectedOrgStructureLevelIds[1]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel3Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel4Name), id: questionIds.OrgLevel4Id })}
                            options={orgLevels && selectedOrgStructureLevelIds[2] &&
                                orgLevels.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[2])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(3, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly || !selectedOrgStructureLevelIds[2]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel4Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Reasons for Referral" id={reasonsForReferralSectionId}>
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.AbsenceReason)}
                            multiline
                            readonly={props.readonly}
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.BodyPartAffected)}
                            filterOldOptions
                            options={BODY_PARTS}
                            readonly={props.readonly}
                            mandatory
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.CurrentWorkStatus)}
                            options={CURRENT_WORK_STATUS}
                            readonly={props.readonly}
                            mandatory
                            hideDivider
                        />
                        {getAnswerToQuestionWithId(currentWorkStatus.id) === currentWorkStatus.notAtWorkAnswer &&
                            <OldDateQuestion
                                questionConfig={getQuestionConfig(questionIds.FirstDayOfAbsence)}
                                readonly={props.readonly}
                                mandatory
                                hideDivider
                            />
                        }
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.AdditionalInformation)}
                            multiline
                            readonly={props.readonly}
                            hideDivider
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Attachments" id={attachmentsSectionId} neverHide>
                        {formQuestions.filter(question => question.answer && (question.answer as FileAnswer).blobId !== undefined)
                            .map(fileQuestion =>
                                (<FileQuestion
                                    questionConfig={({ id: fileQuestion.id, questionText: "" })}
                                    api={`${props.documentBaseApi}/${ohCaseId ? `${ohCaseId}/` : ""}document`}
                                    readonly={props.readonly}
                                    key={fileQuestion.id}
                                    onRemove={() => props.removeQuestion({ sectionId: attachmentsSectionId, id: fileQuestion.id })}
                                    acceptExtensions={acceptableExtensions}
                                />))
                        }
                        {!props.readonly && <Button basic content="Add" onClick={addDocument} />}
                    </OldProgressBarQuestionnaireSection>
                </OldProgressBarQuestionnaireContainer> :
                <Loader active />}
        </Container>
    );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY })),
    removeQuestion: payload => dispatch(removeQuestion({ ...payload, questionnaireKey: PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY })),
    openFirstSection: () => dispatch(openSection({ questionnaireKey: PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY, sectionId: "DataPrivacy" })),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY)),
    loadQuestionnaire: (apiUrl: string, customerId: string, questionnaireId: string, caseId: string) =>
        dispatch(loadQuestionnaireAction(apiUrl, customerId, questionnaireId, caseId, PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY)),
    submitQuestionnaire: (apiUrl: string, questions: AnsweredQuestionWithId[], linkedOhCaseId: string) =>
        dispatch(submitPhysiotherapyReferralQuestionnaire(
            { formData: { questions }, linkedOhCaseId }, apiUrl, "PhysiotherapySelfReferral", PHYSIOTHERAPY_SELF_REFERRAL_QUESTIONNAIRE_KEY))
});

export const PhysiotherapySelfReferral = connect(null, mapDispatchToProps)(PhysiotherapySelfReferralUnconnected);
